/*
  sourceTagging.config.js
  Generosity, Inc. | WTR App | Source Tagging Card v1.7
  ------------------------------------------------------------------
  Single source of truth for:
    1. The ten-pill water source taxonomy
    2. The Bottled brand drawer (eleven brands plus search)
    3. The Trust But Verify scanner bridge content (the reward)
    4. The rotating insight line at the bottom of the card
    5. Client constants for the graduation policy

  v1.7 CHANGES FROM v1.6.1 (verified against the real repo):
  - Brand ids are now the EXACT keys of BOTTLE_BRANDS in
    components/BottleScanView.js ("FIJI Water", "Kirkland
    Signature", ...). This closes scale-debt SD-BRAND-IDS.
  - The app has no URL routing (screens are state inside
    TrustButVerify.js), so buildScannerLink and all hrefs are GONE.
    buildBridge now returns action descriptors; the card invokes an
    onOpenScanner(brandKey, live) callback and TrustButVerify
    performs the screen handoff into BottleScanView.
  - inScanner flags which brands exist in the local scanner
    database. Core Hydration is a real top-consumed brand but is
    not in BOTTLE_BRANDS yet, so it takes the honest branch and
    opens the scanner in search mode.

  RULES BAKED IN:
  - The seven v1.4 labels are preserved verbatim. Additions only.
    Renaming any existing label corrupts the training rows already
    in wtr_test_source_labels. Do not rename. Ever.
  - Evidence-class honesty: the live-vs-label comparison renders
    only when the scanner database holds Label Data for the brand.
    Never fabricate a comparison.
  - Insight lines stay product-neutral. The scanner bridge is the
    one sanctioned crossover, earned by a tag, never pushed.

  Works as a plain browser script or CommonJS module.
*/

(function (root, factory) {
  if (typeof module === "object" && module.exports) {
    module.exports = factory();
  } else {
    root.SourceTagging = factory();
  }
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  /* ================================================================
     1. TEN-PILL TAXONOMY (labels unchanged since v1.5)
     ================================================================ */

  var SOURCE_PILLS = [
    { label: "Tap",             hint: "Straight from the faucet",          legacy: true,  opensBrandDrawer: false },
    { label: "Filtered Tap",    hint: "Pitcher, fridge, or faucet filter", legacy: false, opensBrandDrawer: false },
    { label: "Bottled",         hint: "Any store-bought brand",            legacy: false, opensBrandDrawer: true  },
    { label: "Spring",          hint: "Natural or unbranded spring",       legacy: true,  opensBrandDrawer: false },
    { label: "Alkaline",        hint: "pH-raised or ionized",              legacy: true,  opensBrandDrawer: false },
    { label: "Remineralized",   hint: "Minerals added back",               legacy: true,  opensBrandDrawer: false },
    { label: "Reverse Osmosis", hint: "RO or distilled",                   legacy: true,  opensBrandDrawer: false },
    { label: "Refill Station",  hint: "Kiosk, cooler, or 5-gallon",        legacy: false, opensBrandDrawer: false },
    { label: "Well",            hint: "Private well",                      legacy: true,  opensBrandDrawer: false },
    { label: "River",           hint: "Untreated natural source",          legacy: true,  opensBrandDrawer: false },
  ];

  /* ================================================================
     2. BOTTLED BRAND DRAWER
     `id` values are byte-for-byte the BOTTLE_BRANDS keys in
     components/BottleScanView.js, so the handoff resolves without a
     lookup table. Ordered premium-first for the demographic that
     owns a $199.99 intelligent bottle, anchored by the national
     volume leaders and Kirkland so no mainstream or Costco
     household feels unserved. `category` is the treatment class the
     brand implies, the ML payload the drawer exists to capture.
     ================================================================ */

  var BOTTLED_BRANDS = [
    { id: "FIJI Water",         label: "FIJI",               category: "artesian",               tier: "premium",  inScanner: true  },
    { id: "smartwater",         label: "smartwater",         category: "distilled_remineralized", tier: "premium",  inScanner: true  },
    { id: "Essentia",           label: "Essentia",           category: "alkaline",               tier: "premium",  inScanner: true  },
    { id: "Core Hydration",     label: "CORE Hydration",     category: "purified_ph_balanced",   tier: "premium",  inScanner: false },
    { id: "evian",              label: "evian",              category: "spring",                 tier: "premium",  inScanner: true  },
    { id: "VOSS",               label: "VOSS",               category: "artesian",               tier: "premium",  inScanner: true  },
    { id: "Liquid Death",       label: "Liquid Death",       category: "mountain",               tier: "premium",  inScanner: true  },
    { id: "Aquafina",           label: "Aquafina",           category: "purified",               tier: "national", inScanner: true  },
    { id: "Dasani",             label: "Dasani",             category: "purified_remineralized", tier: "national", inScanner: true  },
    { id: "Poland Spring",      label: "Poland Spring",      category: "spring",                 tier: "national", inScanner: true  },
    { id: "Kirkland Signature", label: "Kirkland Signature", category: "purified",               tier: "value",    inScanner: true  },
  ];

  /* The escape hatch. Free-text entry backed by the scanner's
     API-powered brand search. A wrong brand label is worse than no
     brand label. */
  var MORE_BRANDS = {
    id: "search",
    label: "More brands",
    hint: "Type any brand",
  };

  /* ================================================================
     3. TRUST BUT VERIFY SCANNER BRIDGE (the reward)
     Rendered immediately after a brand tag lands. One tap in, one
     payoff out. buildBridge returns content plus an action name;
     the card maps actions to the onOpenScanner callback and
     TrustButVerify performs the screen handoff.

       action "compare": open BottleScanView on this brand with the
         user's live reading passed as liveComparison, rendering the
         Measured vs Label Data strip above the report.
       action "report": open BottleScanView on this brand (or in
         search mode when inScanner is false), no comparison strip.
     ================================================================ */

  function buildBridge(brand, hasLabelData) {
    if (hasLabelData) {
      return {
        headline: "Nice tag. Now verify it.",
        body: "You just measured " + brand.label + " live. See how your reading compares to what they publish.",
        primaryCta: { label: "Your reading vs their label", action: "compare" },
        secondaryCta: { label: "Full " + brand.label + " Water IQ Report", action: "report" },
      };
    }
    return {
      headline: "You just did what they have not.",
      body: "No published quality data on file for " + brand.label + ". Your live measurement is the record. Trust But Verify.",
      primaryCta: { label: "Full " + brand.label + " Water IQ Report", action: "report" },
      secondaryCta: null,
    };
  }

  /* ================================================================
     4. INSIGHT LINES
     Line 0 is the ML disclosure: first two tags, then every eighth.
     Source and confidence annotations (internal only, never UI):
     L1 CDC NHANES 2011-2014, verified. L2 CDC NHANES 2007-2010,
     corroborated (association, not cause). L3 IBWA 2024 industry
     survey, developing, phrased softly. L4 Leaf Home 2025 vendor
     survey, developing, phrased softly. L5, L6 statements about our
     own pipeline, verified. L7 brand voice, no stat claimed.
     ================================================================ */

  var DISCLOSURE =
    "Tagging your source by hand is temporary. Every tag trains WTR " +
    "Intelligence to recognize your water automatically. Once it " +
    "learns your pattern, this card steps aside.";

  var INSIGHTS = [
    "Federal health data shows just over half of American adults " +
      "drink tap water on any given day. Knowing which water you " +
      "drink is the first step to knowing what is in it.",
    "People with a water filter tend to drink more water overall. " +
      "Whatever your source, tagging it helps you understand it.",
    "Most Americans drink from more than one source every week. " +
      "Tag each one and your water picture gets sharper.",
    "Surveys suggest only about 1 in 10 Americans have tested " +
      "their water in the past year. You just did. Keep going.",
    "Spring, purified, tap, well: each carries its own mineral " +
      "signature. Your tags teach the model to tell them apart.",
    "Every tag links your real water to its municipal record, " +
      "making your next test smarter than your last.",
    "Drink Intelligently starts with knowing your source. " +
      "You are building that knowledge one tag at a time.",
  ];

  var DISCLOSURE_EVERY = 8;
  var STORAGE_KEY = "wtr_source_tag_count";

  /* ================================================================
     5. ROTATION ENGINE plus GRADUATION CLIENT CONSTANTS
     Rotation is a pure function of the device-local tag count.
     Named scale-debt SD-INSIGHT-SYNC: counter is per-device; payoff
     is reading tag count from the per-user rollup once native auth
     lands. Graduation thresholds live SERVER-SIDE ONLY in
     routes/absorptionIntelligence.js; the client only knows the
     confirm timeout and attribution vocabulary.
     ================================================================ */

  var ATTRIBUTION_MODES = { MANUAL: "manual", AUTO: "auto", CONFIRMED: "confirmed" };
  var CONFIRM_TIMEOUT_MS = 4000;

  function getTagCount() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var n = raw == null ? 0 : parseInt(raw, 10);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch (e) {
      return 0;
    }
  }

  function setTagCount(n) {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(n));
    } catch (e) {
      /* Storage denied: rotation degrades to always showing the
         disclosure, which is the safe default. */
    }
  }

  function recordTagAndGetInsight() {
    var count = getTagCount();
    setTagCount(count + 1);
    return getInsightForCount(count);
  }

  function getInsightForCount(count) {
    if (count < 2) return DISCLOSURE;
    if ((count + 1) % DISCLOSURE_EVERY === 0) return DISCLOSURE;
    return INSIGHTS[(count - 2) % INSIGHTS.length];
  }

  function shouldShowFullCard(suggestResponse) {
    return !suggestResponse || suggestResponse.mode !== "suggest";
  }

  return {
    SOURCE_PILLS: SOURCE_PILLS,
    BOTTLED_BRANDS: BOTTLED_BRANDS,
    MORE_BRANDS: MORE_BRANDS,
    buildBridge: buildBridge,
    DISCLOSURE: DISCLOSURE,
    INSIGHTS: INSIGHTS,
    recordTagAndGetInsight: recordTagAndGetInsight,
    getInsightForCount: getInsightForCount,
    getTagCount: getTagCount,
    ATTRIBUTION_MODES: ATTRIBUTION_MODES,
    CONFIRM_TIMEOUT_MS: CONFIRM_TIMEOUT_MS,
    shouldShowFullCard: shouldShowFullCard,
  };
});
